import { Chip } from "@gigpro/ui/Chip";
import { cn } from "@gigpro/ui/tailwind/cn";
import type { CurrentStateEnum } from "@sidegigllc/api/client";

/** States the API is gaining that the generated `CurrentStateEnum` doesn't carry yet. */
type UngeneratedState = "PAUSED" | "EXHAUSTED" | "ERROR";

/**
 * The pro's interview state. Looser than `CurrentStateEnum` because the API widens the
 * enum ahead of the generated client, and a state this build has never seen must still
 * render. Tighten back to `CurrentStateEnum` once the widened enum reaches the client.
 */
export type AiInterviewState = CurrentStateEnum | UngeneratedState | (string & {});

// Maps, not object literals: the key is an untrusted API string, and `{}[state]` would
// return an inherited method for a state like "toString" instead of missing.
const STATE_LABEL = new Map<string, string>([
  ["NONE", "Not invited"],
  ["PENDING", "Invited · pending"],
  ["EXPIRED", "Expired"],
  ["COMPLETED", "Completed"],
  ["PAUSED", "Started · paused"],
  ["EXHAUSTED", "Attempts used up"],
  ["ERROR", "Processing failed"],
]);

/**
 * Colour so an admin can triage the CRM list without reading every chip: blue in flight,
 * yellow stalled, orange needs a re-send, red failed, green done, grey inert. Saturated
 * `tags-*` backgrounds rather than a semantic text token on a pale fill — the latter is
 * too faint to scan a list by at chip size.
 */
const STATE_STYLE = new Map<string, string>([
  ["NONE", "border border-medium-gray bg-transparent text-gray"],
  ["PENDING", "bg-tags-blue text-black"],
  ["EXPIRED", "bg-light-gray text-gray"],
  ["COMPLETED", "bg-tags-green text-black"],
  ["PAUSED", "bg-tags-yellow text-black"],
  ["EXHAUSTED", "bg-tags-orange text-black"],
  ["ERROR", "bg-tags-red text-black"],
]);

/** An unrecognised state gets a label and neutral styling — never blank, never a fake severity. */
const UNKNOWN_STATE_LABEL = "Status unknown";
const UNKNOWN_STATE_STYLE = "border border-medium-gray bg-transparent text-gray";

// Shared status chip so the CRM card and the send dialog read the state identically. The
// dot fills only once completed and inherits the state's own colour.
export function AiInterviewStateChip({ state }: Readonly<{ state: AiInterviewState }>) {
  return (
    <Chip className={cn("gap-1.5 font-regular", STATE_STYLE.get(state) ?? UNKNOWN_STATE_STYLE)}>
      <span
        className={cn(
          "size-1.5 rounded-full border border-current",
          state === "COMPLETED" ? "bg-current" : "bg-transparent",
        )}
      />
      {STATE_LABEL.get(state) ?? UNKNOWN_STATE_LABEL}
    </Chip>
  );
}
